import _ from 'lodash'
import {
	isChartModel,
	isDatasetWidgetModel
} from 'scoreboard/dashboards/widgets/maestro'
import {  tempBreakdownId, tempRangeId  } from 'scoreboard/charts/model/chart-designer-breakdown'
import {
	tempChartDesignerId
} from 'scoreboard/charts/model/chart-designer'
import ChartDesignerFeature from 'scoreboard/charts/model/chart-designer-feature'
const { assignNewIds } = ChartDesignerFeature
import ChartData from 'scoreboard/charts/chart-data/chart-data'
import IdAndTempIdMap from '../../charts/id-tempid-map'
import Backbone from 'backbone'
import unwrapModel from 'scoreboard/components/unwrap-model'
import wrapModel from 'scoreboard/components/wrap-model'
import DatasetTable from 'scoreboard/datasets/model/dataset-table'
import allWidgets from 'scoreboard/dashboards/widgets/all'


function duplicateWobs ({ canvas, copiedWobs, maestro }) {
	let copiedChartsById = _(copiedWobs)
		.filter(wobTypeFilter('chart'))
		.map(w => w.widget.chart)
		.keyBy('id').value()

	let copiedChartsByTempId = _(copiedWobs)
		.filter(wobTypeFilter('chart'))
		.map(w => w.widget.chart)
		.filter(c => !!c.get('tempId'))
		.keyBy(c => c.get('tempId')).value()

	let oldIdToNewChartTempId = {}
	let oldTempIdToNewChartTempId = {}

	let newWobs = _(copiedWobs)
		.filter(w => {
			if (w.get('locked')) {
				// #22580 don't copy locked wobs
				return false
			}

			if (wobType(w) === 'chart-table') {
				let chartId = w.widget.properties.get('chartId')
				let chartTempId = w.widget.properties.get('chartTempId')

				// Don't try to copy a chart table if its chart wasn't copied, too
				return copiedChartsById[chartId] || copiedChartsByTempId[chartTempId]
			} else {
				return true
			}
		})
		.sortBy(copiedWob => {
			let type = wobType(copiedWob)
			if (type === 'chart') {
				// Copy charts before their tables, because we'll need the new
				// chartTempId to set up the table widget
				return -1
			} else if (type === 'chart-table') {
				return 0
			} else {
				return 1
			}
		})
		.map(copiedWob => {
			let copiedWidget = copiedWob.widget
			let ModelClass = allWidgets[copiedWidget.get('type')]
			let widgetAttrs = _.omit(_.cloneDeep(copiedWidget.toJSON()), 'id', 'cid')
			// Pass in the copied widget's data as a widget attribute; this is what's done on
			// initial render of the dashboard, and it saves us from querying the server
			widgetAttrs.data = copiedWidget.data.get()

			let widgetOptions =
				// #59377 omit the parentData from the copied widget options
				_.cloneDeep(_.omit(copiedWidget.options, 'parentData'))

			let type = wobType(copiedWob)

			let oldChartIds
			if (type === 'chart') {
				oldChartIds = replaceChartWidgetIdsWithTempIds(widgetAttrs, copiedWobs)
			} else if (type === 'chart-table' || type === 'chart-color-legend') {
				adjustPastedChartAccessory(widgetAttrs, copiedChartsById, copiedChartsByTempId, oldIdToNewChartTempId, oldTempIdToNewChartTempId)
			} else if (type === 'report') {
				adjustPastedReport(widgetAttrs, widgetOptions, copiedWidget)
			} else if (type === 'dataset-table') {
				adjustPastedTable(widgetAttrs)
			} else if (type === 'dataset-filter') {
				adjustPastedFilter(widgetAttrs, maestro)
			}

			let newWidget = new ModelClass(widgetAttrs, widgetOptions)

			let newWob = {
				// Place the new wob slightly down/left from the old ones (similar to
				// what we do when consecutively adding multiple new widgets)
				left: copiedWob.get('left') + 10,
				top: copiedWob.get('top') + 10,
				// Increment the z-index so the new ones are on top
				'z-index': copiedWob.get('z-index') + 1,
				widget: newWidget
			}

			_.extend(newWob, _.pick(copiedWob.attributes,
				'height', 'width', 'rotate', 'levitated', 'outlineWidth', 'outlineColor'))

			if (type === 'chart') {
				if (oldChartIds.id) {
					oldIdToNewChartTempId[oldChartIds.id] = widgetAttrs.chart.tempId
				} else if (oldChartIds.tempId) {
					oldTempIdToNewChartTempId[oldChartIds.tempId] = widgetAttrs.chart.tempId
				}

				// Make sure the new chart has its own ChartData instance, so
				// it doesn't share data with the source widget
				const chart = newWidget.chart
				chart.data = new ChartData({ dashboardData: newWidget.data })
			}

			duplicateThresholdColoring(copiedWob, newWob)

			return newWob
		}).value()

	// #31072 Maestro needs to do queries for any new dataset widgets. However,
	// it needs the widget Views to do this, which don't exist yet (we're just
	// returning Models). So we'll wait until canvas has had time to do its
	// work, then tell Maestro to run the query
	process.nextTick(() => {
		_(newWobs)
			.map(wob => wob.widget)
			.filter(widget => isDatasetWidgetModel(widget))
			.map(widgetModel => mapModelToView(widgetModel, canvas))
			.groupBy(widgetView => {
				// #58591 when duplicating dataset speedometers and gauges, the
				// datasetId function is found on the widget model
				// (data-source-widget-model.js) rather than on the widget view
				// so we need to check both the view and the model
				let datasetId
				if (_.isFunction(widgetView?.datasetId)) {
					datasetId = widgetView.datasetId()
				} else if (_.isFunction(widgetView?.model?.datasetId)) {
					datasetId = widgetView.model.datasetId()
				} else {
					console.warn('Unable to get datasetId for widget view', widgetView)
				}
				return datasetId
			})
			.toPairs()
			.forEach(([ datasetId, views ]) => {
				maestro.query(datasetId, views)
			})

		fetchCharts({ canvas, maestro, newWobs })
	})

	return newWobs
}

function fetchCharts ({ canvas, maestro, newWobs }) {
	_(newWobs)
		.map(wob => wob.widget)
		.filter(widget => isChartModel(widget))
		.map(widgetModel => mapModelToView(widgetModel, canvas))
		.forEach(chartView => {
			maestro.fetchChart(chartView)
		})
}

function mapModelToView (widgetModel, canvas) {
	return _(canvas.views).values()
		.find(view => view.model.widget.cid == widgetModel.cid)
		.view
}

function wobType (wob) {
	return wob.widget.properties.get('type')
}

/**
 * Given a widget type, will return a predicate function that will return true
 * for any wob models matching that type
 */
function wobTypeFilter (type) {
	return function (wob) {
		return wobType(wob) === type
	}
}

function replaceChartWidgetIdsWithTempIds (widgetAttrs, copiedWobs) {
	delete widgetAttrs._id

	// Note the original chart's tempId so we can link up any copied
	// chart-tables later
	let oldChartId = widgetAttrs.chart.id
	let oldChartTempId = widgetAttrs.chart.tempId

	replaceChartIdsWithTempIds(widgetAttrs.chart)

	delete widgetAttrs.properties.chartId
	delete widgetAttrs.properties.chartTempId

	if (widgetAttrs.chart.chartTable) {
		// Original chart widget had chartTable: true, but we can only
		// do that on the new copy if the chart-table was copied, too.
		// If not, set chartTable to false
		let copiedTable = !!_.find(copiedWobs, wob => {
			if (wobType(wob) !== 'chart-table') {
				return false
			}

			if (!_.isNil(oldChartId) && wob.widget.properties.get('chartId') == oldChartId) {
				return true
			} else if (!_.isNil(oldChartTempId) && wob.widget.properties.get('chartTempId') == oldChartTempId) {
				return true
			} else {
				return false
			}
		})

		widgetAttrs.chart.chartTable = copiedTable
	}

	return { id: oldChartId, tempId: oldChartTempId }
}

function replaceChartIdsWithTempIds(chartDesignerAttrs) {
	delete chartDesignerAttrs.id
	chartDesignerAttrs.tempId = tempChartDesignerId()

	const oldRangeIdToNewTempId = new IdAndTempIdMap()
	const oldBreakdownIdToNewTempId = new IdAndTempIdMap()
	const oldFeatureIdToNewTempId = new IdAndTempIdMap()

	chartDesignerAttrs.breakdowns.forEach(breakdown => {
		const oldIds = { oldId: breakdown.id, oldTempId: breakdown.tempId }
		breakdown.tempId = tempBreakdownId()
		oldBreakdownIdToNewTempId.addIds(oldIds, breakdown)
		delete breakdown.id

		breakdown.ranges.forEach(range => {
			const oldIds = { oldId: range.id, oldTempId: range.tempId }
			range.tempId = tempRangeId()
			oldRangeIdToNewTempId.addIds(oldIds, range)
			delete range.id
		})
		_.each(breakdown.datasetFields, f => { // #42519
			// these will be assigned when saved, if we don't null them out
			// then we get PK Constraints
			delete f.id
			delete f.breakdownId
		})
	})

	chartDesignerAttrs.colors.forEach(color => {
		delete color.chartDesignerId
	})

	const postProcessingOperations = []
	chartDesignerAttrs.features.forEach(f => {
		const oldIds = { oldId: f.id, oldTempId: f.tempId }
		assignNewIds(f, oldRangeIdToNewTempId, postProcessingOperations)
		oldFeatureIdToNewTempId.addIds(oldIds, f)
	})
	_.each(postProcessingOperations, op => op(oldFeatureIdToNewTempId))

	chartDesignerAttrs.labels.forEach((label, idx) => {
		label.id = -1 - idx
		delete label.chartDesignerId
	})

	chartDesignerAttrs.colors.forEach((color, idx) => {
		color.id = -1 - idx
		delete color.chartDesignerId
		if (!!color.thresholdColoring) delete color.thresholdColoring.id
	})

	chartDesignerAttrs.legends.forEach((legend, idx) => {
		legend.id = -1 - idx
		delete legend.chartDesignerId
	})

	// now that we have remapped feature IDs, we can remap the
	// references in breakdown.datasetFields
	chartDesignerAttrs.breakdowns.forEach(breakdown => {
		_.each(breakdown.datasetFields, f => {
			const newIds = oldFeatureIdToNewTempId.getNewIds({ id: f.serieId, tempId: f.serieTempId })
			f.serieTempId = newIds ? newIds.tempId : null
			delete f.serieId
		})
	})

	adjustChartSortSettings(({ chartDesignerAttrs, oldFeatureIdToNewTempId,
		oldBreakdownIdToNewTempId, oldRangeIdToNewTempId }))

	return chartDesignerAttrs
}

function adjustChartSortSettings (
	{ chartDesignerAttrs, oldFeatureIdToNewTempId, oldBreakdownIdToNewTempId, oldRangeIdToNewTempId }) {

	// #49439 clear serie sort settings IDs so they save as new ones, and
	// remap any feature/breakdown/range references

	function adjustSortByValueRange (r) {
		delete r.id

		let newBreakdownIds = oldBreakdownIdToNewTempId.getNewIds({ id: r.breakdownId, tempId: r.breakdownTempId })
		delete r.breakdownId
		r.breakdownTempId = newBreakdownIds.tempId

		let newRangeIds = oldRangeIdToNewTempId.getNewIds({ id: r.rangeId, tempId: r.rangeTempId })
		delete r.rangeId
		r.rangeTempId = newRangeIds.tempId
	}

	;['left', 'right'].forEach(axis => {
		let settings = chartDesignerAttrs[axis + 'AxisBarSortSettings']
		if (settings) {
			delete settings.id
			settings.sortByValueRanges?.forEach(adjustSortByValueRange)
		}
	})

	chartDesignerAttrs.breakdowns.forEach(breakdown => {
		if (breakdown.sortSettings) {
			let settings = breakdown.sortSettings
			delete settings.id

			const newIds = oldFeatureIdToNewTempId.getNewIds(
				{ id: settings.featureId, tempId: settings.featureTempId })
			settings.featureTempId = newIds ? newIds.tempId : null
			delete settings.featureId

			if (settings.oppositeBreakdownSortByValueRange) {
				adjustSortByValueRange(settings.oppositeBreakdownSortByValueRange)
			}
		}
	})
}

function adjustPastedChartAccessory (widgetAttrs, copiedChartsById, copiedChartsByTempId, oldIdToNewChartTempId, oldTempIdToNewChartTempId) {
	// Find the chart for the original chart-table widget
	let oldChart
	if (widgetAttrs.properties.chartId) {
		oldChart = copiedChartsById[widgetAttrs.properties.chartId]
	} else if (widgetAttrs.properties.chartTempId) {
		oldChart = copiedChartsByTempId[widgetAttrs.properties.chartTempId]
	}

	// Set the chartTempId property to the new chart corresponding to
	// the copied chart-table's chart
	let newChartTempId = oldChart.get('id')
		? oldIdToNewChartTempId[oldChart.get('id')]
		: oldTempIdToNewChartTempId[oldChart.get('tempId')]
	widgetAttrs.properties.chartTempId = newChartTempId
	delete widgetAttrs.properties.chartId
}

function adjustPastedReport (widgetAttrs, widgetOptions, copiedWidget) {
	// Clear out the subtypeId; it will be set later after the server saves it
	delete widgetAttrs.properties.reportSubtypeId

	// Create a deep copy of the original report, clearing out its
	// id so a new one will be saved
	widgetOptions.report = _.cloneDeep(copiedWidget.report.toJSON())
	delete widgetOptions.report.subtypeId
}

function adjustPastedFilter (widgetAttrs, maestro) {
	const datasetId = widgetAttrs.properties.datasetId
	const filter = JSON.parse(widgetAttrs.properties.filter)

	const maxLinkGroup = _(maestro.liveFilters()[datasetId]).map('linkGroup').max()
	if (maxLinkGroup) {
		filter.linkGroup = maxLinkGroup + 1
	}

	// #57964 the filter in a new filter widget will by definition not have been
	// created by a drilldown action, so drilldownActionId should be cleared
	filter.drilldownActionId = null

	widgetAttrs.properties.filter = JSON.stringify(filter)
}

function adjustPastedTable (widgetAttrs) {
	const deleteIdsFromTable = (table) => {
		const isModel = table instanceof Backbone.Model
		if (isModel) {
			table = unwrapModel(table)
		}

		delete table.id
		delete _.get(table, 'repeatingRowGrouping', {}).id
		delete _.get(table, 'repeatingColumnGrouping', {}).id

		_.get(table, 'aggregations', []).forEach(aggregation => {
			delete aggregation.id
			delete aggregation.tableId
			_.get(aggregation, 'filters', []).forEach(filter => {
				delete filter.id
			})
			_.get(aggregation, 'denominatorFilters', []).forEach(filter => {
				delete filter.id
			})
		})

		// need to re-wrap the table if it was a model originally, since stuff down the
		// line could rely in it being a model
		if (isModel) {
			table = wrapModel(table, DatasetTable)
		}

		return table
	}

	// #49506 remove all the ids from the table and any associated tables in data, so all
	// new ids are generated on save
	widgetAttrs.table = deleteIdsFromTable(widgetAttrs.table)
	if (!_.isNil(_.get(widgetAttrs, 'data.tables'))) {
		widgetAttrs.data.tables = widgetAttrs.data.tables.map(table => deleteIdsFromTable(table))
	}
}

function duplicateThresholdColoring (oldWob, newWob) {
	let oldColoring = oldWob.get('thresholdColoring')
	if (!oldColoring) {
		return
	}

	let newColoring = _.cloneDeep(oldColoring)
	newColoring.id = null
	newWob.thresholdColoring = newColoring
}

function factory ({ maestro }) {
	return function (canvas, copiedWobs) {
		return duplicateWobs({ canvas, copiedWobs, maestro })
	}
}

duplicateWobs.factory = factory

export default duplicateWobs
export { replaceChartIdsWithTempIds }
export { adjustPastedFilter }
duplicateWobs.__filename__ = 'dashboards/edit/duplicate-wobs.js'
